import React, { useState, useEffect, useCallback } from 'react';
import { formatDateLocale } from '../utils/dateFormat';
import { Link } from 'react-router-dom';
import { AlertTriangle, Users, MessageCircle, FileText, Zap } from 'lucide-react';
import api from '../utils/api';


function getAuthHeaders() {
  const token = '';
  return {};
}

export function useQuotaSummary() {
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchQuota = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/clinic/quota-summary`);
      {
        const d = res.data;
        if (d.success) setQuota(d);
      }
    } catch (_) { /* best-effort */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchQuota(); }, [fetchQuota]);

  return { quota, loading, refetchQuota: fetchQuota };
}

function QuotaItem({ icon: Icon, label, current, max, isExceeded }) {
  const display = max === null || max === undefined
    ? `${current}`
    : `${current}/${max}`;
  return (
    <span className="flex items-center gap-1 text-[10px]" data-testid={`quota-item-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <Icon className="h-3 w-3 text-slate-400" />
      <span className="text-slate-500">{label}:</span>
      <span className={`font-semibold ${isExceeded ? 'text-amber-600' : 'text-slate-600'}`}>{display}</span>
    </span>
  );
}

export default function ClinicQuotaStrip({ quota }) {
  if (!quota) return null;

  const active = quota.enforcement_active;
  const leads = quota.leads || {};
  const threads = quota.threads || {};
  const quotes = quota.quotes || {};
  const campaigns = quota.campaigns || {};

  const leadsExceeded = active && leads.remaining !== null && leads.remaining !== undefined && leads.remaining <= 0;
  const threadsExceeded = active && threads.remaining !== null && threads.remaining !== undefined && threads.remaining <= 0;
  const quotesExceeded = active && quotes.remaining !== null && quotes.remaining !== undefined && quotes.remaining <= 0;

  // If leads feature disabled
  if (active && leads.enabled === false) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="leads-disabled-banner">
        <AlertTriangle className="h-3.5 w-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-xs font-medium text-amber-800">Leads not available on your plan</p>
          <p className="text-[10px] text-amber-600">Upgrade to enable lead capture and messaging.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4 flex-wrap" data-testid="clinic-quota-strip">
      <QuotaItem icon={Users} label="Leads" current={leads.current_month ?? 0} max={leads.monthly_max} isExceeded={leadsExceeded} />
      <span className="h-3 w-px bg-slate-200" />
      <QuotaItem icon={MessageCircle} label="Active threads" current={threads.current_active ?? 0} max={threads.active_max} isExceeded={threadsExceeded} />
      <span className="h-3 w-px bg-slate-200" />
      <QuotaItem icon={FileText} label="Quotes" current={quotes.current_month ?? 0} max={quotes.monthly_max} isExceeded={quotesExceeded} />
      {campaigns.enforcement_active !== false && (
        <>
          <span className="h-3 w-px bg-slate-200" />
          <Link
            to="/clinic/campaigns"
            className="rounded hover:bg-slate-100 px-1 -mx-1 transition-colors focus:outline-none focus:ring-1 focus:ring-fuchsia-300"
            data-testid="quota-campaigns-link"
            title="Manage campaigns"
          >
            <QuotaItem icon={Zap} label="Campaigns" current={campaigns.active_current ?? 0} max={campaigns.active_limit >= 0 ? campaigns.active_limit : null} isExceeded={false} />
          </Link>
        </>
      )}
    </div>
  );
}

export function isQuotaExceeded(quota, type) {
  if (!quota || !quota.enforcement_active) return false;
  const section = quota[type];
  if (!section) return false;
  if (section.remaining !== null && section.remaining !== undefined && section.remaining <= 0) return true;
  if (type === 'leads' && section.enabled === false) return true;
  return false;
}

export function handleQuotaError(response, toastFn) {
  if (!response) return false;
  const err = response?.detail || response;
  if (!err?.code) return false;

  const messages = {
    TX_QUOTA_FEATURE_DISABLED: 'This feature is not available on your current plan. Upgrade to enable.',
    TX_QUOTA_LEADS_MONTHLY_EXCEEDED: `Monthly lead limit reached (${err.current}/${err.limit}). Resets ${err.reset_at_utc ? formatDateLocale(err.reset_at_utc) : 'next month'}.`,
    TX_QUOTA_ACTIVE_THREADS_EXCEEDED: `Active thread limit reached (${err.current}/${err.limit}). Resolve existing threads to create new ones.`,
    TX_QUOTA_QUOTES_MONTHLY_EXCEEDED: `Monthly quote limit reached (${err.current}/${err.limit}). Resets ${err.reset_at_utc ? formatDateLocale(err.reset_at_utc) : 'next month'}.`,
    TX_QUOTA_UNAVAILABLE: 'Service temporarily unavailable. Please try again later.',
  };

  const msg = messages[err.code] || err.message || 'Quota exceeded';
  if (toastFn) toastFn({ title: 'Limit Reached', description: msg, variant: 'destructive' });
  return true;
}
